import React from 'react';
import { Box, TextField, Typography, Divider, InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { TextFieldWithPopup } from './TextFieldWithPopup';
import { parseDate } from './ExitPlanFormSection.utils';

export const EXEMPTION_FIELD_LABELS = {
  SECTION: 'Exemption Request Details',
  HINT: 'For an exemption request the "Liquidation Plan" field above holds the "Extension or exemption request" free text.',
  TO_IMPACT: 'TO impact (Euros)',
  EXCEPTION_START: 'Exception start date *',
  EXCEPTION_END: 'Exception end date *',
  JUSTIFICATION: 'Justification *',
  SAFETY_REF: 'Pharos/Pelican safety assessment reference number',
  PRODUCT_TYPES: 'Product types',
  BRANDS: 'Brands',
};

/**
 * The attributes that turn an exit plan into an exemption request.
 * Must be rendered inside the LocalizationProvider of ExitPlanFormSection.
 */
export const ExemptionFieldsSection = ({
  toImpactEuros,
  exceptionStartDate,
  exceptionEndDate,
  justification,
  safetyAssessmentReference,
  productTypes,
  brands,
  setToImpactEuros,
  setExceptionStartDate,
  setExceptionEndDate,
  setJustification,
  setSafetyAssessmentReference,
  setProductTypes = () => {},
  setBrands = () => {},
  formErrors = {},
}) => (
  <Box sx={styles.container}>
    <Divider sx={{ my: 1 }} />
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      {EXEMPTION_FIELD_LABELS.SECTION}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {EXEMPTION_FIELD_LABELS.HINT}
    </Typography>

    <Box sx={styles.row}>
      <DatePicker
        label={EXEMPTION_FIELD_LABELS.EXCEPTION_START}
        value={parseDate(exceptionStartDate)}
        onChange={setExceptionStartDate}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            error: Boolean(formErrors.exceptionStartDate),
            helperText: formErrors.exceptionStartDate || '',
          },
        }}
      />
      <DatePicker
        label={EXEMPTION_FIELD_LABELS.EXCEPTION_END}
        value={parseDate(exceptionEndDate)}
        onChange={setExceptionEndDate}
        minDate={parseDate(exceptionStartDate) || undefined}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            error: Boolean(formErrors.exceptionEndDate),
            helperText: formErrors.exceptionEndDate || '',
          },
        }}
      />
    </Box>

    <Box sx={styles.row}>
      <TextField
        label={EXEMPTION_FIELD_LABELS.TO_IMPACT}
        type="number"
        size="small"
        fullWidth
        value={toImpactEuros ?? ''}
        onChange={(e) => setToImpactEuros(e.target.value)}
        inputProps={{ min: 0, step: '0.01' }}
        InputProps={{ endAdornment: <InputAdornment position="end">EUR</InputAdornment> }}
        error={Boolean(formErrors.toImpactEuros)}
        helperText={formErrors.toImpactEuros || ''}
      />
      <TextField
        label={EXEMPTION_FIELD_LABELS.SAFETY_REF}
        size="small"
        fullWidth
        value={safetyAssessmentReference ?? ''}
        onChange={(e) => setSafetyAssessmentReference(e.target.value)}
      />
    </Box>

    <Box sx={styles.row}>
      <TextField
        label={EXEMPTION_FIELD_LABELS.PRODUCT_TYPES}
        size="small"
        fullWidth
        value={productTypes ?? ''}
        onChange={(e) => setProductTypes(e.target.value)}
      />
      <TextField
        label={EXEMPTION_FIELD_LABELS.BRANDS}
        size="small"
        fullWidth
        value={brands ?? ''}
        onChange={(e) => setBrands(e.target.value)}
      />
    </Box>

    <TextFieldWithPopup
      label={EXEMPTION_FIELD_LABELS.JUSTIFICATION}
      value={justification}
      onChange={(e) => setJustification(e.target.value)}
      error={Boolean(formErrors.justification)}
      helperText={formErrors.justification || ''}
      required
    />
  </Box>
);

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 2 },
  row: { display: 'flex', gap: 2 },
};

export default ExemptionFieldsSection;
